import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import type * as React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import type { SortDirection } from "@/hooks/use-data-grid";
import { cn } from "@/lib/utils";

interface DataTableHeader {
  label: string;
  className?: string;
  /**
   * When set, the header becomes a sort toggle. Must match a server `sort`
   * value (i.e. a key in the endpoint's sort-column allowlist). Requires the
   * `sort`/`sortDir`/`onSort` props to be passed.
   */
  sortKey?: string;
}

interface DataTableProps {
  headers: DataTableHeader[];
  children: React.ReactNode;
  loading?: boolean;
  empty?: React.ReactNode;
  colSpan?: number;
  className?: string;
  /** Active sort key — wire from `useDataGrid().sort`. */
  sort?: string;
  /** Active sort direction — wire from `useDataGrid().sortDir`. */
  sortDir?: SortDirection;
  /** Sort handler — wire from `useDataGrid().toggleSort`. */
  onSort?: (key: string) => void;
}

export function DataTable({
  headers,
  children,
  loading = false,
  empty,
  colSpan,
  className,
  sort,
  sortDir,
  onSort,
}: DataTableProps) {
  const span = colSpan ?? headers.length;
  const hasChildren = Array.isArray(children)
    ? children.filter(Boolean).length > 0
    : Boolean(children);
  const showEmpty = !loading && !hasChildren && empty;

  return (
    <div className={cn("overflow-x-auto rounded-lg border", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            {headers.map((header) => {
              const sortKey = header.sortKey;
              const active = sortKey !== undefined && sort === sortKey;
              return (
                <th
                  key={header.label || header.sortKey}
                  aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                  className={cn(
                    "px-4 py-3 text-left text-xs font-medium text-muted-foreground",
                    header.className,
                  )}
                >
                  {sortKey !== undefined && onSort !== undefined ? (
                    <button
                      type="button"
                      onClick={() => onSort(sortKey)}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {header.label}
                      {active ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="size-3 opacity-50" />
                      )}
                    </button>
                  ) : (
                    header.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className="border-b last:border-b-0">
                {headers.map((_header, colIndex) => (
                  <td key={`skeleton-${rowIndex}-${colIndex}`} className="px-4 py-3">
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : showEmpty ? (
            <tr>
              <td colSpan={span}>{empty}</td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}
